import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Laptop,
  Globe,
  Activity,
  ShieldCheck,
  Cpu,
  HardDrive,
  Database,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Filter,
  X,
  Info,
  CheckCircle2
} from 'lucide-react';
import { api as apiClient } from '../../lib/api';

export interface TimelineSession {
  sessionId: string;
  workspaceId: string;
  userId: string;
  endpointId?: string;
  sessionType: string;
  resourceName: string;
  applicationName?: string;
  bundleId?: string;
  sourceComponent?: string;
  hostname?: string;
  currentDomain?: string;
  registrableDomain?: string;
  startTime: string;
  endTime: string;
  durationSeconds: number;
  isIdle?: boolean;
  isLocked?: boolean;
  truthType: string;
  confidenceLevel: string;
  confidenceReason?: string;
  reconstructionVersion?: number;
}

export interface DeviceInfo {
  endpointId: string;
  hostname?: string;
  platform?: string;
  sourceComponent?: string;
  osName?: string;
  osVersion?: string;
  currentState: string;
  currentAppName?: string;
  currentDomain?: string;
  cpuUsagePercent?: number;
  memoryUsagePercent?: number;
  diskUsagePercent?: number;
  enrollmentStatus?: string;
  agentHealth?: string;
  telemetryConfidence?: string;
  lastHeartbeatAt?: string;
}

export interface EmployeeActivityData {
  userId: string;
  date: string;
  timezone?: string;
  summary?: {
    activeSeconds: number;
    idleSeconds: number;
    firstSeenAt?: string;
    lastSeenAt?: string;
    applicationUsage?: string | Record<string, number>;
    domainUsage?: string | Record<string, number>;
  };
  timelineDtos: TimelineSession[];
  deviceDtos: DeviceInfo[];
  confidence: string;
}

interface EmployeeDetailViewProps {
  workspaceId: string;
  userId: string;
  dateStr?: string;
  onBack: () => void;
}

type FilterType = 'ALL' | 'APP' | 'DOMAIN' | 'IDLE' | 'TAURI' | 'EXTENSION';
type ViewTab = 'TIMELINE' | 'BROWSER_DOMAINS' | 'DEVICES' | 'QUALITY';

export const EmployeeDetailView: React.FC<EmployeeDetailViewProps> = ({
  workspaceId,
  userId,
  dateStr = new Date().toISOString().split('T')[0],
  onBack,
}) => {
  const [data, setData] = useState<EmployeeActivityData | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(dateStr || '');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedApp, setExpandedApp] = useState<string | null>('Google Chrome');
  const [timelineFilter, setTimelineFilter] = useState<FilterType>('ALL');
  const [activeTab, setActiveTab] = useState<ViewTab>('TIMELINE');
  const [selectedSession, setSelectedSession] = useState<TimelineSession | null>(null);

  const fetchActivity = async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      setError(null);
      const res = await apiClient.workforce.getEmployeeActivity(userId, workspaceId, selectedDate);
      setData(res);
    } catch (err: any) {
      console.error('Failed to fetch employee activity:', err);
      if (!isSilent) setError(err?.message || 'Unable to load employee activity details.');
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
    const intervalId = setInterval(() => fetchActivity(true), 5000);
    return () => clearInterval(intervalId);
  }, [workspaceId, userId, selectedDate]);

  const formatDuration = (seconds: number) => {
    if (!seconds || seconds <= 0) return '0m';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs > 0 ? `${secs}s` : ''}`.trim();
    return `${secs}s`;
  };

  const formatTime = (isoString?: string) => {
    if (!isoString) return '—';
    try {
      return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return isoString;
    }
  };

  const parseJsonMap = (raw?: string | Record<string, number>): Record<string, number> => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  };

  const appUsageMap = parseJsonMap(data?.summary?.applicationUsage);
  const domainUsageMap = parseJsonMap(data?.summary?.domainUsage);

  const totalAppSeconds = Object.values(appUsageMap).reduce((a, b) => a + b, 0) || 1;

  const activeDevice = data?.deviceDtos?.find(d => d.currentState === 'ONLINE_ACTIVE' || d.currentState === 'ONLINE_IDLE') || data?.deviceDtos?.[0];
  const isOnline = activeDevice?.currentState === 'ONLINE_ACTIVE';
  const isIdleState = activeDevice?.currentState === 'ONLINE_IDLE';

  // Filtered timeline
  const rawTimeline = data?.timelineDtos || [];

  const isDomainSession = (sess: TimelineSession) => {
    const st = (sess.sessionType || '').toUpperCase();
    const sc = (sess.sourceComponent || '').toUpperCase();
    const res = (sess.resourceName || '').toLowerCase();
    const app = (sess.applicationName || sess.resourceName || '').toLowerCase();
    const isDomainRes = res.includes('.') && !res.endsWith('.exe') && !res.endsWith('.app');
    const isBrowserApp = app.includes('chrome') || app.includes('browser') || app.includes('firefox') || app.includes('safari') || app.includes('edge') || app.includes('brave') || app.includes('arc');
    return st === 'WEB_DOMAIN' || st === 'BROWSER_DOMAIN' || st === 'DOMAIN' || sc === 'CHROME_EXTENSION' || Boolean(sess.registrableDomain) || isDomainRes || isBrowserApp;
  };

  const filteredTimeline = rawTimeline.filter((sess) => {
    const isDomain = isDomainSession(sess);
    const isIdle = sess.isIdle || sess.sessionType === 'IDLE';
    const source = sess.sourceComponent || 'UNKNOWN';

    switch (timelineFilter) {
      case 'APP':
        return !isDomain && !isIdle;
      case 'DOMAIN':
        return isDomain;
      case 'IDLE':
        return isIdle;
      case 'TAURI':
        return source === 'TAURI_AGENT';
      case 'EXTENSION':
        return source === 'CHROME_EXTENSION';
      case 'ALL':
      default:
        return true;
    }
  }).sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

  const domainSessions = rawTimeline.filter(isDomainSession);
  const appSessions = rawTimeline.filter((s) => !isDomainSession(s) && !s.isIdle && s.sessionType !== 'IDLE');

  return (
    <div className="space-y-6">
      {/* Navigation & Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-900 border border-slate-800 rounded-xl p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-950 hover:bg-slate-800 border border-slate-800 rounded-lg text-slate-300 text-xs font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Workforce Overview</span>
          </button>

          <div>
            <h1 className="text-lg font-bold text-slate-100 flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-400" />
              Employee Activity Intelligence
            </h1>
            <p className="text-xs text-slate-400">
              User ID: <span className="font-mono text-indigo-300">{userId}</span>
              {data?.timezone && (
                <span className="ml-2 px-2 py-0.5 bg-slate-800 rounded text-[10px] font-mono text-slate-400">
                  TZ: {data.timezone}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-slate-950 border border-slate-800 px-3 py-1.5 rounded-lg text-xs">
            <Calendar className="w-4 h-4 text-indigo-400 shrink-0" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-transparent text-slate-100 font-semibold focus:outline-none cursor-pointer"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center bg-slate-900/50 border border-slate-800 rounded-xl text-slate-400">
          Loading complete activity timeline & live state...
        </div>
      ) : error ? (
        <div className="p-4 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl flex items-center gap-2 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      ) : (
        <>
          {/* Live Telemetry State Panel */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider">LIVE TELEMETRY STATE</span>
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full ${activeDevice?.agentHealth === 'STALE'
                    ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    : isOnline
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      : isIdleState
                        ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                    }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${activeDevice?.agentHealth === 'STALE'
                      ? 'bg-amber-400'
                      : isOnline
                        ? 'bg-emerald-400 animate-ping'
                        : 'bg-slate-400'
                      }`}
                  />
                  {activeDevice?.agentHealth === 'STALE' ? 'STALE' : activeDevice?.currentState || 'OFFLINE'}
                </span>

                {data?.deviceDtos?.some((d: DeviceInfo) => d.platform === 'BROWSER_EXTENSION' || d.sourceComponent === 'CHROME_EXTENSION') ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                    <Globe className="w-3 h-3 text-emerald-400" />
                    Extension Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400 bg-slate-800 border border-slate-700 px-2 py-0.5 rounded-full">
                    <Globe className="w-3 h-3 text-slate-400" />
                    Extension Not Connected
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 px-2.5 py-0.5 rounded-md">
                  <ShieldCheck className="w-3.5 h-3.5 text-indigo-400" />
                  Confidence: {data?.confidence || 'HIGH'}
                </span>
                <span className="text-slate-500">
                  Last Heartbeat:{' '}
                  <span className="text-slate-300 font-mono">
                    {activeDevice?.lastHeartbeatAt ? formatTime(activeDevice.lastHeartbeatAt) : 'Unavailable'}
                  </span>
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
              <div className="bg-slate-950/60 p-3 rounded-lg border border-slate-800/80 space-y-1">
                <span className="text-slate-500 block">Device Hostname</span>
                <span className="font-semibold text-slate-200 truncate block">
                  {activeDevice?.hostname || activeDevice?.endpointId || '—'}
                </span>
              </div>

              <div className="bg-slate-950/60 p-3 rounded-lg border border-slate-800/80 space-y-1">
                <span className="text-slate-500 block">Current Foreground App</span>
                <span className="font-semibold text-indigo-300 flex items-center gap-1.5 truncate">
                  <Laptop className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                  {activeDevice?.currentAppName || '—'}
                </span>
              </div>

              <div className="bg-slate-950/60 p-3 rounded-lg border border-slate-800/80 space-y-1">
                <span className="text-slate-500 block">Current Domain</span>
                <span className="font-semibold text-emerald-400 font-mono flex items-center gap-1.5 truncate">
                  <Globe className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  {activeDevice?.currentDomain ? (
                    activeDevice.currentDomain
                  ) : activeDevice?.currentAppName?.toLowerCase().includes('chrome') ? (
                    <span className="text-slate-400 font-sans text-xs italic">Browser Extension context unavailable</span>
                  ) : (
                    '—'
                  )}
                </span>
              </div>

              <div className="bg-slate-950/60 p-3 rounded-lg border border-slate-800/80 space-y-1">
                <span className="text-slate-500 block">Telemetry Source</span>
                <span className="font-medium text-slate-300 block font-mono text-indigo-400">
                  {activeDevice?.sourceComponent || activeDevice?.platform || '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Daily Activity Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-1">
              <span className="text-xs text-slate-400 block">Work Time / Active</span>
              <span className="text-xl font-bold font-mono text-emerald-400">
                {formatDuration(data?.summary?.activeSeconds || 0)}
              </span>
            </div>

            <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-1">
              <span className="text-xs text-slate-400 block">Idle Time</span>
              <span className="text-xl font-bold font-mono text-amber-400">
                {formatDuration(data?.summary?.idleSeconds || 0)}
              </span>
            </div>

            <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-1">
              <span className="text-xs text-slate-400 block">Recorded Sessions</span>
              <span className="text-xl font-bold font-mono text-indigo-400">
                {rawTimeline.length}
              </span>
            </div>

            <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-1">
              <span className="text-xs text-slate-400 block">First Seen</span>
              <span className="text-sm font-semibold font-mono text-slate-200">
                {data?.summary?.firstSeenAt ? formatTime(data.summary.firstSeenAt) : '—'}
              </span>
            </div>

            <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-1">
              <span className="text-xs text-slate-400 block">Last Seen</span>
              <span className="text-sm font-semibold font-mono text-slate-200">
                {data?.summary?.lastSeenAt ? formatTime(data.summary.lastSeenAt) : '—'}
              </span>
            </div>
          </div>

          {/* Main View Area */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Column 1: Application Usage & Registered Endpoints */}
            <div className="lg:col-span-1 space-y-6">
              {/* Application Usage */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4">
                <h3 className="text-sm font-semibold text-slate-100 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Laptop className="w-4 h-4 text-indigo-400" />
                    Application Usage Breakdown
                  </span>
                  <span className="text-xs text-slate-400 font-mono">{selectedDate}</span>
                </h3>

                {Object.keys(appUsageMap).length === 0 ? (
                  <div className="text-xs text-slate-500 p-4 text-center">No application activity recorded for this period.</div>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(appUsageMap)
                      .sort(([, a], [, b]) => b - a)
                      .map(([appName, seconds]) => {
                        const pct = Math.round((seconds / totalAppSeconds) * 100);
                        const isBrowser = appName.toLowerCase().includes('chrome') || appName.toLowerCase().includes('browser');
                        const isExpanded = expandedApp === appName;

                        return (
                          <div key={appName} className="space-y-1.5">
                            <div
                              onClick={() => isBrowser && setExpandedApp(isExpanded ? null : appName)}
                              className={`flex items-center justify-between text-xs cursor-pointer p-1.5 rounded transition-colors ${isBrowser ? 'hover:bg-slate-800/60' : ''
                                }`}
                            >
                              <span className="font-medium text-slate-200 flex items-center gap-1.5">
                                {isBrowser && (
                                  isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-indigo-400" /> : <ChevronRight className="w-3.5 h-3.5 text-indigo-400" />
                                )}
                                {appName}
                              </span>
                              <div className="flex items-center gap-2 font-mono text-slate-400">
                                <span>{formatDuration(seconds)}</span>
                                <span className="text-[10px] text-slate-500 w-8 text-right">{pct}%</span>
                              </div>
                            </div>

                            {/* Progress bar */}
                            <div className="w-full bg-slate-950 h-1.5 rounded-full overflow-hidden">
                              <div
                                className="bg-indigo-500 h-full rounded-full transition-all"
                                style={{ width: `${Math.min(100, Math.max(5, pct))}%` }}
                              />
                            </div>

                            {/* Expandable Browser Domain Usage */}
                            {isBrowser && isExpanded && Object.keys(domainUsageMap).length > 0 && (
                              <div className="ml-4 pt-2 space-y-2 border-l border-indigo-500/20 pl-3">
                                <span className="text-[11px] font-semibold text-slate-400 flex items-center gap-1">
                                  <Globe className="w-3 h-3 text-emerald-400" />
                                  Browser Domain Breakdown:
                                </span>
                                {Object.entries(domainUsageMap)
                                  .sort(([, a], [, b]) => b - a)
                                  .map(([domain, domSec]) => (
                                    <div key={domain} className="flex items-center justify-between text-[11px] font-mono text-slate-300 bg-slate-950/80 px-2.5 py-1.5 rounded border border-slate-800">
                                      <span className="text-emerald-300 truncate max-w-[150px]">{domain}</span>
                                      <span className="text-slate-400">{formatDuration(domSec)}</span>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Registered Devices Panel */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4">
                <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2">
                  <Database className="w-4 h-4 text-indigo-400" />
                  Registered Native Devices ({data?.deviceDtos?.length || 0})
                </h3>

                {!data?.deviceDtos || data.deviceDtos.length === 0 ? (
                  <div className="text-xs text-slate-500 p-4 text-center bg-slate-950/40 rounded-lg">
                    No native devices registered for this employee.
                  </div>
                ) : (
                  data.deviceDtos.map((dev) => (
                    <div key={dev.endpointId} className="bg-slate-950 p-4 rounded-xl border border-slate-800 space-y-3 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-100 truncate max-w-[180px]">
                          {dev.hostname || dev.endpointId}
                        </span>
                        <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full font-mono text-[10px]">
                          {dev.enrollmentStatus || 'REGISTERED'}
                        </span>
                      </div>

                      <div className="text-[11px] text-slate-400 space-y-1">
                        <div>Platform: <span className="text-slate-200 font-mono">{dev.platform || dev.osName || '—'}</span></div>
                        <div>Source: <span className="text-slate-200 font-mono">{dev.sourceComponent || '—'}</span></div>
                        <div>Last Sync: <span className="text-slate-200 font-mono">{dev.lastHeartbeatAt ? formatTime(dev.lastHeartbeatAt) : '—'}</span></div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-800/80 text-[11px]">
                        <div>
                          <span className="text-slate-500 block flex items-center gap-1">
                            <Cpu className="w-3 h-3 text-indigo-400" /> CPU
                          </span>
                          <span className="font-mono text-slate-200 font-semibold">
                            {dev.cpuUsagePercent !== undefined && dev.cpuUsagePercent !== null ? `${dev.cpuUsagePercent}%` : '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500 block flex items-center gap-1">
                            <Activity className="w-3 h-3 text-emerald-400" /> RAM
                          </span>
                          <span className="font-mono text-slate-200 font-semibold">
                            {dev.memoryUsagePercent !== undefined && dev.memoryUsagePercent !== null ? `${dev.memoryUsagePercent}%` : '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500 block flex items-center gap-1">
                            <HardDrive className="w-3 h-3 text-amber-400" /> Disk
                          </span>
                          <span className="font-mono text-slate-200 font-semibold">
                            {dev.diskUsagePercent !== undefined && dev.diskUsagePercent !== null ? `${dev.diskUsagePercent}%` : '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Data Quality Panel */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3">
                <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  Telemetry Data Quality
                </h3>

                <div className="space-y-2 text-xs">
                  <div className="flex justify-between text-slate-400">
                    <span>Reconstructed Sessions:</span>
                    <span className="font-mono font-semibold text-slate-200">{rawTimeline.length}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Browser Domain Sessions:</span>
                    <span className="font-mono font-semibold text-emerald-400">{domainSessions.length}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Native Application Sessions:</span>
                    <span className="font-mono font-semibold text-indigo-400">{appSessions.length}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Truth & Confidence:</span>
                    <span className="font-mono font-semibold text-emerald-400">{data?.confidence || 'HIGH'}</span>
                  </div>
                  <div className="flex justify-between text-slate-400 pt-2 border-t border-slate-800">
                    <span>Ledger Reconciliation:</span>
                    <span className="font-mono text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" /> Deterministic
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Column 2: Chronological Activity Timeline & Browser Activity (2/3 width) */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4">
                {/* View Tabs & Section Header */}
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800/80 pb-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setActiveTab('TIMELINE')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeTab === 'TIMELINE'
                        ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                        : 'bg-slate-950 text-slate-400 hover:bg-slate-800 border border-slate-800'
                        }`}
                    >
                      <Clock className="w-3.5 h-3.5" />
                      Chronological Activity Timeline
                    </button>

                    <button
                      onClick={() => setActiveTab('BROWSER_DOMAINS')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeTab === 'BROWSER_DOMAINS'
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        : 'bg-slate-950 text-slate-400 hover:bg-slate-800 border border-slate-800'
                        }`}
                    >
                      <Globe className="w-3.5 h-3.5" />
                      Browser Activity ({domainSessions.length})
                    </button>
                  </div>

                  <span className="text-xs text-slate-400 font-mono">
                    {filteredTimeline.length} / {rawTimeline.length} Sessions Displayed
                  </span>
                </div>

                {activeTab === 'TIMELINE' ? (
                  <>
                    {/* Timeline Filter Bar */}
                    <div className="flex flex-wrap items-center gap-2 bg-slate-950 p-2 rounded-lg border border-slate-800 text-xs">
                      <span className="text-slate-400 font-medium px-2 flex items-center gap-1">
                        <Filter className="w-3 h-3 text-slate-400" /> Filter:
                      </span>
                      {(['ALL', 'APP', 'DOMAIN', 'IDLE', 'TAURI', 'EXTENSION'] as FilterType[]).map((f) => {
                        const labels: Record<FilterType, string> = {
                          ALL: 'All Activity',
                          APP: 'Applications',
                          DOMAIN: 'Websites / Domains',
                          IDLE: 'Idle Periods',
                          TAURI: 'TAURI_AGENT',
                          EXTENSION: 'CHROME_EXTENSION',
                        };

                        return (
                          <button
                            key={f}
                            onClick={() => setTimelineFilter(f)}
                            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${timelineFilter === f
                              ? 'bg-indigo-500 text-white font-semibold shadow-sm'
                              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/80'
                              }`}
                          >
                            {labels[f]}
                          </button>
                        );
                      })}
                    </div>

                    {/* Timeline List */}
                    {filteredTimeline.length === 0 ? (
                      <div className="p-12 text-center text-slate-400 text-xs bg-slate-950/40 rounded-lg border border-slate-800/80 space-y-2">
                        <Info className="w-6 h-6 text-slate-500 mx-auto" />
                        <p className="font-semibold text-slate-300">No activity sessions match the selected filter for this date.</p>
                        <p className="text-slate-500">Try changing the date or clearing timeline filters.</p>
                      </div>
                    ) : (
                      <div className="relative border-l border-indigo-500/20 ml-4 space-y-4 pl-6 pt-2">
                        {filteredTimeline.map((sess) => {
                          const isDomain = sess.sessionType === 'WEB_DOMAIN';
                          const isIdle = sess.isIdle || sess.sessionType === 'IDLE';

                          return (
                            <div key={sess.sessionId} className="relative group">
                              {/* Circle marker */}
                              <div
                                className={`absolute -left-[31px] top-2 w-3 h-3 rounded-full ring-4 ring-slate-900 ${isIdle
                                  ? 'bg-amber-400'
                                  : isDomain
                                    ? 'bg-emerald-400'
                                    : 'bg-indigo-500'
                                  }`}
                              />

                              <div
                                onClick={() => setSelectedSession(sess)}
                                className="p-4 bg-slate-950 border border-slate-800 group-hover:border-indigo-500/50 rounded-xl space-y-2 transition-all cursor-pointer shadow-sm hover:shadow-md"
                              >
                                <div className="flex items-center justify-between text-xs">
                                  <div className="flex items-center gap-2">
                                    {isIdle ? (
                                      <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                                    ) : isDomain ? (
                                      <Globe className="w-4 h-4 text-emerald-400 shrink-0" />
                                    ) : (
                                      <Laptop className="w-4 h-4 text-indigo-400 shrink-0" />
                                    )}

                                    <div>
                                      <span className="font-semibold text-slate-100 text-sm">
                                        {sess.resourceName}
                                      </span>
                                      {sess.registrableDomain && sess.registrableDomain !== sess.resourceName && (
                                        <span className="ml-2 text-[11px] text-slate-400 font-mono">
                                          ({sess.registrableDomain})
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <span className="font-mono text-indigo-300 font-semibold bg-indigo-500/10 px-2.5 py-1 rounded border border-indigo-500/20 text-xs">
                                    {formatDuration(sess.durationSeconds)}
                                  </span>
                                </div>

                                <div className="flex flex-wrap items-center justify-between text-[11px] text-slate-400 pt-2 border-t border-slate-900">
                                  <span className="font-mono text-slate-300 font-medium">
                                    {formatTime(sess.startTime)} – {formatTime(sess.endTime)}
                                  </span>

                                  <div className="flex items-center gap-3">
                                    <span className="text-slate-500">
                                      Source:{' '}
                                      <span className="text-slate-300 font-mono">
                                        {sess.sourceComponent || 'UNKNOWN'}
                                      </span>
                                    </span>

                                    <span className="flex items-center gap-1 text-emerald-400 font-mono">
                                      <ShieldCheck className="w-3 h-3" />
                                      {sess.truthType || 'DIRECT'} ({sess.confidenceLevel || 'HIGH'})
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  /* Dedicated Browser Activity Breakdown Table */
                  <div className="space-y-6">
                    {/* Domain or Browser App Usage Breakdown */}
                    {Object.keys(domainUsageMap).length > 0 ? (
                      <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-3">
                        <h4 className="text-xs font-semibold text-slate-200 flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-emerald-400">
                            <Globe className="w-4 h-4 text-emerald-400" />
                            Website & Domain Active Time Breakdown
                          </span>
                          <span className="text-[11px] font-mono text-slate-400">
                            {Object.keys(domainUsageMap).length} Websites Tracked
                          </span>
                        </h4>

                        <div className="space-y-2">
                          {(() => {
                            const totalDomSec = Object.values(domainUsageMap).reduce((a, b) => a + b, 0) || 1;
                            return Object.entries(domainUsageMap)
                              .sort(([, a], [, b]) => b - a)
                              .map(([domainName, seconds]) => {
                                const pct = Math.round((seconds / totalDomSec) * 100);
                                return (
                                  <div key={domainName} className="p-2.5 bg-slate-900/80 rounded-lg border border-slate-800 space-y-1.5">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="font-semibold text-emerald-300 font-mono flex items-center gap-1.5">
                                        <Globe className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                                        {domainName}
                                      </span>
                                      <div className="flex items-center gap-3 font-mono">
                                        <span className="text-slate-200 font-bold">{formatDuration(seconds)}</span>
                                        <span className="text-[10px] text-slate-400 w-10 text-right">{pct}%</span>
                                      </div>
                                    </div>
                                    <div className="w-full bg-slate-950 h-1.5 rounded-full overflow-hidden">
                                      <div
                                        className="bg-emerald-400 h-full rounded-full transition-all"
                                        style={{ width: `${Math.min(100, Math.max(4, pct))}%` }}
                                      />
                                    </div>
                                  </div>
                                );
                              });
                          })()}
                        </div>
                      </div>
                    ) : (
                      (() => {
                        const browserAppEntries = Object.entries(appUsageMap).filter(([appName]) => {
                          const lower = appName.toLowerCase();
                          return lower.includes('chrome') || lower.includes('browser') || lower.includes('firefox') || lower.includes('safari') || lower.includes('edge') || lower.includes('brave') || lower.includes('arc');
                        });

                        if (browserAppEntries.length === 0) return null;

                        const totalBrowserSec = browserAppEntries.reduce((acc, [, s]) => acc + s, 0) || 1;

                        return (
                          <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-3">
                            <h4 className="text-xs font-semibold text-slate-200 flex items-center justify-between">
                              <span className="flex items-center gap-1.5 text-emerald-400">
                                <Globe className="w-4 h-4 text-emerald-400" />
                                Browser Application Active Time Breakdown
                              </span>
                              <span className="text-[11px] font-mono text-slate-400">
                                {browserAppEntries.length} Browser(s) Tracked
                              </span>
                            </h4>

                            <div className="space-y-2">
                              {browserAppEntries
                                .sort(([, a], [, b]) => b - a)
                                .map(([appName, seconds]) => {
                                  const pct = Math.round((seconds / totalBrowserSec) * 100);
                                  return (
                                    <div key={appName} className="p-2.5 bg-slate-900/80 rounded-lg border border-slate-800 space-y-1.5">
                                      <div className="flex items-center justify-between text-xs">
                                        <span className="font-semibold text-indigo-300 font-mono flex items-center gap-1.5">
                                          <Laptop className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                                          {appName}
                                        </span>
                                        <div className="flex items-center gap-3 font-mono">
                                          <span className="text-slate-200 font-bold">{formatDuration(seconds)}</span>
                                          <span className="text-[10px] text-slate-400 w-10 text-right">{pct}%</span>
                                        </div>
                                      </div>
                                      <div className="w-full bg-slate-950 h-1.5 rounded-full overflow-hidden">
                                        <div
                                          className="bg-indigo-500 h-full rounded-full transition-all"
                                          style={{ width: `${Math.min(100, Math.max(4, pct))}%` }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        );
                      })()
                    )}

                    {/* Timeline Domain Sessions Table */}
                    {domainSessions.length > 0 ? (
                      <div className="space-y-2">
                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-1">
                          Chronological Domain Session Logs ({domainSessions.length})
                        </h4>
                        <div className="overflow-x-auto border border-slate-800 rounded-xl bg-slate-950">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-slate-900 border-b border-slate-800 text-slate-400 uppercase font-mono text-[10px]">
                              <tr>
                                <th className="p-3">Time Period</th>
                                <th className="p-3">Browser / App</th>
                                <th className="p-3">Hostname</th>
                                <th className="p-3">Registrable Domain</th>
                                <th className="p-3">Duration</th>
                                <th className="p-3">Source</th>
                                <th className="p-3">Confidence</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/80 font-mono">
                              {domainSessions.map((ds) => (
                                <tr key={ds.sessionId} className="hover:bg-slate-900/50 transition-colors">
                                  <td className="p-3 text-slate-300 whitespace-nowrap">
                                    {formatTime(ds.startTime)} – {formatTime(ds.endTime)}
                                  </td>
                                  <td className="p-3 text-indigo-300 font-sans font-medium">
                                    {ds.applicationName || 'Google Chrome'}
                                  </td>
                                  <td className="p-3 text-emerald-400 font-semibold">{ds.hostname || ds.resourceName}</td>
                                  <td className="p-3 text-slate-400">{ds.registrableDomain || '—'}</td>
                                  <td className="p-3 text-slate-200 font-semibold">{formatDuration(ds.durationSeconds)}</td>
                                  <td className="p-3 text-indigo-400 text-[11px]">{ds.sourceComponent || 'CHROME_EXTENSION'}</td>
                                  <td className="p-3 text-emerald-400 text-[11px]">
                                    {ds.confidenceLevel || 'HIGH'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : Object.keys(domainUsageMap).length === 0 ? (
                      <div className="p-12 text-center text-slate-400 text-xs bg-slate-950/40 rounded-lg space-y-2 border border-slate-800">
                        <Globe className="w-6 h-6 text-slate-500 mx-auto" />
                        <p className="font-semibold text-slate-300">No browser domain activity recorded for this date.</p>
                        <p className="text-slate-500">Connect the Sahaj Connect Chrome Extension or visit websites to start tracking domain activity.</p>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Event Details Modal / Drawer */}
      {selectedSession && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl relative">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="text-base font-bold text-slate-100 flex items-center gap-2">
                <Info className="w-5 h-5 text-indigo-400" />
                Telemetry Event Session Details
              </h3>
              <button
                onClick={() => setSelectedSession(null)}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3 bg-slate-950 p-3 rounded-lg border border-slate-800">
                <div>
                  <span className="text-slate-500 block">Resource / Domain</span>
                  <span className="font-semibold text-emerald-400 font-mono text-sm block truncate">
                    {selectedSession.resourceName}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Session Type</span>
                  <span className="font-semibold text-slate-200 font-mono text-sm block">
                    {selectedSession.sessionType}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 bg-slate-950 p-3 rounded-lg border border-slate-800">
                <div>
                  <span className="text-slate-500 block">Start Time</span>
                  <span className="font-mono text-slate-200">{formatTime(selectedSession.startTime)}</span>
                  <span className="text-[10px] text-slate-500 block">{selectedSession.startTime}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">End Time</span>
                  <span className="font-mono text-slate-200">{formatTime(selectedSession.endTime)}</span>
                  <span className="text-[10px] text-slate-500 block">{selectedSession.endTime}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 bg-slate-950 p-3 rounded-lg border border-slate-800">
                <div>
                  <span className="text-slate-500 block">Duration</span>
                  <span className="font-mono font-bold text-indigo-300 text-sm">
                    {formatDuration(selectedSession.durationSeconds)} ({selectedSession.durationSeconds}s)
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Source Component</span>
                  <span className="font-mono text-indigo-400 font-semibold">
                    {selectedSession.sourceComponent || 'UNKNOWN'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 bg-slate-950 p-3 rounded-lg border border-slate-800">
                <div>
                  <span className="text-slate-500 block">Hostname</span>
                  <span className="font-mono text-slate-300">{selectedSession.hostname || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Registrable Domain</span>
                  <span className="font-mono text-emerald-400">{selectedSession.registrableDomain || '—'}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 bg-slate-950 p-3 rounded-lg border border-slate-800">
                <div>
                  <span className="text-slate-500 block">Truth Type</span>
                  <span className="font-mono text-slate-200">{selectedSession.truthType}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Confidence Level</span>
                  <span className="font-mono text-emerald-400 font-semibold">{selectedSession.confidenceLevel}</span>
                </div>
              </div>

              {selectedSession.confidenceReason && (
                <div className="bg-slate-950 p-3 rounded-lg border border-slate-800">
                  <span className="text-slate-500 block">Confidence Reason</span>
                  <span className="text-slate-300 italic">{selectedSession.confidenceReason}</span>
                </div>
              )}

              <div className="bg-slate-950 p-3 rounded-lg border border-slate-800 font-mono text-[11px] text-slate-400 space-y-1">
                <div>Session ID: <span className="text-slate-300">{selectedSession.sessionId}</span></div>
                <div>Endpoint ID: <span className="text-slate-300">{selectedSession.endpointId || '—'}</span></div>
                <div>Reconstruction Version: <span className="text-slate-300">v{selectedSession.reconstructionVersion || 1}</span></div>
              </div>
            </div>

            <div className="pt-2 text-right">
              <button
                onClick={() => setSelectedSession(null)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs rounded-lg transition-colors"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDetailView;
